import { useQueryClient } from '@tanstack/react-query'
import {
  ChevronDown,
  ChevronRight,
  CircleAlert,
  CircleMinus,
  CirclePlus,
  Pencil,
  RotateCcw,
  TriangleAlert,
} from 'lucide-react'
import pluralize from 'pluralize'
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, type PathValue, useForm, useWatch } from 'react-hook-form'
import { toast } from 'sonner'

import { OnboardingBannerSlot } from '@/components/OnboardingBannerSlot'
import { useRequoteOnSave } from '@/components/quotes/summary/requoteOnSave'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CurrencyInput } from '@/components/ui/CurrencyInput'
import { Dialog, DialogClose, FormDialogContent } from '@/components/ui/dialog'
import { Dropzone } from '@/components/ui/dropzone'
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Item, ItemContent, ItemDescription, ItemMedia, ItemTitle } from '@/components/ui/item'
import { Pagination } from '@/components/ui/pagination'
import { ResponsiveTable, type ResponsiveTableColumn } from '@/components/ui/responsive-table'
import { SearchInput } from '@/components/ui/search-input'
import { SelectWithMore, SimpleSelect } from '@/components/ui/select-with-more'
import { Separator } from '@/components/ui/separator'
import { Spinner } from '@/components/ui/spinner'
import { StateSelect } from '@/components/ui/StateSelect'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { SEARCH_MIN_ROWS, useTableSearch } from '../hooks/useTableSearch'
import { CacheKeys, invalidateApplicationSummary } from '../lib/cache'
import { PARSER_ACCEPTED_FILE_TYPES } from '../lib/fileUpload'
import { listSentence } from '../lib/formatters'
import {
  FULL_VIN_LENGTH,
  sanitizeLicensePlate,
  sanitizeLicenseState,
  sanitizeVin,
  vinCheckDigitWarning,
  vinError,
} from '../lib/sanitization'
import { cn, enumToOptions, formatCurrency } from '../lib/utils'
import {
  decodeVin,
  type Equipment,
  EquipmentStatus,
  priceEstimateLabel,
  suggestTrailerValue,
  suggestTruckValue,
  useWeightClassesConfig,
  VehicleClass,
  VehicleClassLabels,
  type VinDecodeResult,
  WeightClass,
  WeightClassLabels,
} from '../models/equipment'
import { useCurrentUser } from '../models/user'
import { EquipmentDetailsDialog } from './EquipmentDetailsDialog'
import { PendingChangeActions, type PendingChangeKind } from './PendingChangeActions'
import { UploadIconButton } from './UploadIconButton'
import { YearSelect } from './YearSelect'

export interface EquipmentTableConfig {
  type: 'trucks' | 'trailers'
  label: string
  labelPlural: string
  vehicleClasses: VehicleClass[]
  commonVehicleClasses: VehicleClass[]
  /** Example make/model shown as placeholders, so a trailer form doesn't suggest a tractor. */
  makePlaceholder: string
  modelPlaceholder: string
  /** Names the class field for the equipment at hand rather than "Vehicle". */
  classFieldLabel: string
  /** What carriers need that the item lacks, in the words its card shows them; none means it's complete. */
  missingFields: (item: Equipment) => string[]
  createEmpty: () => Equipment
  upload?: (file: File, orgId: string) => Promise<{ items: Equipment[]; warning?: string }>
  apiAdd?: (data: Partial<Equipment>, orgId: string) => Promise<Equipment>
  apiEdit?: (id: string, data: Partial<Equipment>, orgId: string) => Promise<Equipment>
  apiDelete?: (id: string, orgId: string) => Promise<void>
  apiFetchRemoved?: (orgId: string) => Promise<Equipment[]>
  apiRestore?: (id: string, orgId: string) => Promise<Equipment>
  apiApprove?: (orgId: string, id: string) => Promise<Equipment>
  apiReject?: (orgId: string, id: string) => Promise<Equipment>
}

export interface EquipmentTableProps {
  items: Equipment[]
  setItems: (items: Equipment[]) => void
  handleSubmit?: (items: Equipment[]) => void | Promise<void>
  footer?: React.ReactNode
  onDelete?: (item: Equipment) => void
  onEdit?: (item: Equipment) => void
  onAdd?: (item: Equipment) => void
  onRestore?: (item: Equipment) => void
  addCtaOnTop?: boolean
  config: EquipmentTableConfig
  readOnly?: boolean
  /** Greys out add, upload, edit, remove and restore; rows still open their details. */
  changesDisabled?: boolean
  orgId: string
  hideHeading?: boolean
  /** Defaults to "Confirm your trucks" (or trailers). */
  heading?: string
  /** Shows a search field above the table (customer + internal customer pages). */
  searchable?: boolean
  /** Onboarding confirmation step only: requires the fields carriers need. */
  enforceCarrierRequired?: boolean
  /** Intake only: one header row (the heading, search from 5 rows, Add and Import), empty or not. */
  compact?: boolean
  /** Intake only: shown beside the compact heading, e.g. how many are incomplete. */
  notice?: React.ReactNode
}

/** Blank required field — something the user still has to supply. */
function MissingValue() {
  return <TriangleAlert className="text-warning-d1 h-5 w-5" />
}

/** Blank optional field — nothing owed, so a dash rather than a warning. */
function BlankValue() {
  return <span className="text-muted-foreground">—</span>
}

/** The value, labelled when it is Cai's estimate rather than a number someone entered. */
function PriceCell({ item }: { item: Equipment }) {
  const estimate = priceEstimateLabel(item.datePriceEstimated)
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      {formatCurrency(item.price ?? 0)}
      {estimate && <Badge variant={estimate === 'Estimated' ? 'secondary' : 'warning'}>{estimate}</Badge>}
    </span>
  )
}

function vehicleClassLabel(item: Equipment) {
  return item.vehicleClass ? VehicleClassLabels[item.vehicleClass as keyof typeof VehicleClassLabels] : null
}

function weightClassLabel(item: Equipment) {
  return item.weightClass ? WeightClassLabels[item.weightClass as keyof typeof WeightClassLabels] : null
}

/**
 * Year, make, and model together identify the vehicle, so they lead the card as its title. Where a row's banner
 * says what's missing a blank one is left out; elsewhere it's flagged.
 */
function equipmentTitle(item: Equipment, flagBlank: boolean) {
  if (flagBlank) {
    return (
      <>
        {item.year || <MissingValue />} {item.make || <MissingValue />} {item.model || <MissingValue />}
      </>
    )
  }
  return [item.year, item.make, item.model].filter(Boolean).join(' ') || item.vehicleNumber || 'Vehicle'
}

// The confirm checks only look at active equipment.
function equipmentIssue(item: Equipment, config: EquipmentTableConfig): string | null {
  const missing = item.status === EquipmentStatus.ACTIVE ? config.missingFields(item) : []
  return missing.length > 0 ? `Add ${listSentence(missing)}` : null
}

function equipmentColumns(config: EquipmentTableConfig): ResponsiveTableColumn<Equipment>[] {
  return [
    { key: 'make', header: 'Make', hideOnCard: true, cell: (item) => item.make || <MissingValue /> },
    { key: 'model', header: 'Model', hideOnCard: true, cell: (item) => item.model || <MissingValue /> },
    { key: 'year', header: 'Year', hideOnCard: true, cell: (item) => item.year || <MissingValue /> },
    { key: 'vehicleClass', header: 'Class', cell: (item) => vehicleClassLabel(item) ?? <MissingValue /> },
    { key: 'licenseState', header: 'State', cell: (item) => item.licenseState || <BlankValue /> },
    { key: 'licensePlate', header: 'License plate', cell: (item) => item.licensePlate || <BlankValue /> },
    ...(config.type === 'trucks'
      ? [
          {
            key: 'weightClass',
            header: 'Weight',
            cell: (item: Equipment) => weightClassLabel(item) ?? <MissingValue />,
          },
        ]
      : []),
    {
      key: 'price',
      header: 'Value',
      cell: (item) => (item.price != null ? <PriceCell item={item} /> : <BlankValue />),
    },
    { key: 'vin', header: 'VIN', cell: (item) => item.vin || <MissingValue /> },
  ]
}

function removedEquipmentColumns(config: EquipmentTableConfig): ResponsiveTableColumn<Equipment>[] {
  return [
    { key: 'make', header: 'Make', hideOnCard: true, cell: (item) => item.make },
    { key: 'model', header: 'Model', hideOnCard: true, cell: (item) => item.model },
    { key: 'year', header: 'Year', hideOnCard: true, cell: (item) => item.year },
    { key: 'vehicleClass', header: 'Class', cell: (item) => vehicleClassLabel(item) ?? '—' },
    { key: 'licenseState', header: 'State', cell: (item) => item.licenseState || <BlankValue /> },
    { key: 'licensePlate', header: 'License plate', cell: (item) => item.licensePlate || <BlankValue /> },
    ...(config.type === 'trucks'
      ? [{ key: 'weightClass', header: 'Weight', cell: (item: Equipment) => weightClassLabel(item) ?? '—' }]
      : []),
    { key: 'vin', header: 'VIN', cell: (item) => item.vin },
  ]
}

function equipmentSearchValues(item: Equipment) {
  return [
    item.vehicleNumber,
    item.vin,
    item.make,
    item.model,
    item.year,
    item.licensePlate,
    item.licenseState,
    item.vehicleClass ? VehicleClassLabels[item.vehicleClass] : null,
  ]
}

export function EquipmentTable({
  addCtaOnTop,
  handleSubmit,
  items,
  setItems,
  footer,
  onDelete,
  onEdit,
  onAdd,
  onRestore,
  config,
  readOnly,
  changesDisabled,
  orgId,
  hideHeading,
  heading = `Confirm your ${config.labelPlural}`,
  searchable,
  enforceCarrierRequired,
  compact,
  notice,
}: EquipmentTableProps) {
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isConfirming, setIsConfirming] = useState(false)
  const requote = useRequoteOnSave()
  const [editItem, setEditItem] = useState<Equipment | null>(null)
  const [detailsItemId, setDetailsItemId] = useState<string | null>(null)
  // An edit started from the details view goes back to it when the form closes.
  const [editFromDetails, setEditFromDetails] = useState(false)
  const [addItem, setAddItem] = useState<Equipment | null>(null)
  const [isImporting, setIsImporting] = useState(false)
  const [removedItems, setRemovedItems] = useState<Equipment[]>([])
  const [showRemoved, setShowRemoved] = useState(false)
  const { user } = useCurrentUser({ enabled: true })
  const isCaiAdmin = !!user?.isCaiAdmin
  const queryClient = useQueryClient()
  const { query, onQueryChange, page, setPage, totalPages, pageItems, rangeSummary } = useTableSearch({
    items,
    getSearchValues: equipmentSearchValues,
  })
  // Carrier-required checks match the backend's confirm-step gate, which only looks at ACTIVE equipment.
  const activeItems = items.filter((item) => item.status === EquipmentStatus.ACTIVE)
  const isComplete = (item: Equipment) => config.missingFields(item).length === 0
  const allValid =
    !enforceCarrierRequired ||
    (activeItems.every(isComplete) && (config.type === 'trucks' ? activeItems.length > 0 : true))
  const errors = enforceCarrierRequired ? activeItems.filter((item) => !isComplete(item)) : []
  const hasPending = items.some((item) => item.status === EquipmentStatus.PENDING_ADD || !!item.pendingChange)
  const columns = equipmentColumns(config)
  // Read from the live list, so the view shows a save, or the pending change it created, once it lands.
  const detailsItem = items.find((item) => item.id === detailsItemId) ?? null

  function openEditFromDetails(item: Equipment) {
    setDetailsItemId(null)
    setEditFromDetails(true)
    setEditItem(item)
  }

  function closeEdit() {
    if (editFromDetails && editItem) setDetailsItemId(editItem.id)
    setEditFromDetails(false)
    setEditItem(null)
  }

  const removedToggle = (
    <Button
      variant="link"
      size="sm"
      className="text-muted-foreground h-auto px-0 font-medium"
      onClick={() => setShowRemoved(!showRemoved)}
    >
      {showRemoved ? <ChevronDown /> : <ChevronRight />}
      Removed {config.labelPlural} ({removedItems.length})
    </Button>
  )

  function renderRowActions(item: Equipment) {
    const pending = getPending(item)
    if (pending) {
      return (
        <PendingChangeActions
          kind={pending.kind}
          isCaiAdmin={isCaiAdmin}
          onApprove={() => handleApprove(pending.id)}
          onReject={() => handleReject(pending.id)}
        />
      )
    }
    if (readOnly) return null

    return (
      <>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="iconOnly"
              className="hover:text-brand-800 text-neutral-500"
              onClick={() => setEditItem(item)}
              disabled={changesDisabled}
              aria-label={`Edit ${config.label}`}
            >
              <Pencil />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{`Edit ${config.label}`}</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="iconOnly"
              className="hover:text-error text-neutral-500"
              onClick={() => handleDelete(item)}
              disabled={changesDisabled}
              aria-label={`Remove ${config.label}`}
            >
              <CircleMinus />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{`Remove ${config.label}`}</TooltipContent>
        </Tooltip>
      </>
    )
  }

  function getPending(item: Equipment): { id: string; kind: PendingChangeKind } | null {
    if (item.status === EquipmentStatus.PENDING_ADD) {
      return { id: item.id, kind: 'add' }
    }
    if (item.pendingChange) {
      return {
        id: item.pendingChange.id,
        kind: item.pendingChange.status === EquipmentStatus.PENDING_REMOVE ? 'remove' : 'change',
      }
    }
    return null
  }

  async function handleApprove(pendingId: string) {
    if (!config.apiApprove) return
    await config.apiApprove(orgId, pendingId)
    queryClient.invalidateQueries({
      queryKey: [config.type === 'trucks' ? CacheKeys.TRUCKS : CacheKeys.TRAILERS, orgId],
    })
    invalidateApplicationSummary(queryClient, orgId)
  }

  async function handleReject(pendingId: string) {
    if (!config.apiReject) return
    await config.apiReject(orgId, pendingId)
    queryClient.invalidateQueries({
      queryKey: [config.type === 'trucks' ? CacheKeys.TRUCKS : CacheKeys.TRAILERS, orgId],
    })
    invalidateApplicationSummary(queryClient, orgId)
  }

  useEffect(() => {
    if (config.apiFetchRemoved) {
      config
        .apiFetchRemoved(orgId)
        .then(setRemovedItems)
        .catch(() => {})
    }
  }, [orgId])

  function refreshRemoved() {
    if (config.apiFetchRemoved) {
      config
        .apiFetchRemoved(orgId)
        .then(setRemovedItems)
        .catch(() => {})
    }
  }

  function handleDelete(item: Equipment) {
    if (config.apiDelete) {
      config
        .apiDelete(item.id, orgId)
        .then(() => {
          queryClient.invalidateQueries({
            queryKey: [config.type === 'trucks' ? CacheKeys.TRUCKS : CacheKeys.TRAILERS, orgId],
          })
          invalidateApplicationSummary(queryClient, orgId)
          refreshRemoved()
          onDelete?.(item)
        })
        .catch(() => {
          toast.error('Error removing equipment')
        })
    }
  }

  function handleRestore(item: Equipment) {
    if (config.apiRestore) {
      config
        .apiRestore(item.id, orgId)
        .then(() => {
          queryClient.invalidateQueries({
            queryKey: [config.type === 'trucks' ? CacheKeys.TRUCKS : CacheKeys.TRAILERS, orgId],
          })
          invalidateApplicationSummary(queryClient, orgId)
          refreshRemoved()
          onRestore?.(item)
        })
        .catch(() => {
          toast.error('Error restoring equipment')
        })
    }
  }

  async function handleEditSubmit(item: Equipment) {
    if (config.apiEdit) {
      await config.apiEdit(item.id, item, orgId)
    }
    setItems(items.map((t) => (t.id === item.id ? item : t)))
    // Re-sync from the server: under an active policy the edit doesn't touch the
    // live row (a PENDING_CHANGE clone is created instead), so the row must reload
    // to show the pending badge rather than the locally-edited values.
    queryClient.invalidateQueries({
      queryKey: [config.type === 'trucks' ? CacheKeys.TRUCKS : CacheKeys.TRAILERS, orgId],
    })
    invalidateApplicationSummary(queryClient, orgId)
    closeEdit()
    onEdit?.(item)
  }

  async function handleAddSubmit(item: Equipment) {
    const savedItem = config.apiAdd ? await config.apiAdd(item, orgId) : item
    setItems([...items, savedItem])
    queryClient.invalidateQueries({
      queryKey: [config.type === 'trucks' ? CacheKeys.TRUCKS : CacheKeys.TRAILERS, orgId],
    })
    invalidateApplicationSummary(queryClient, orgId)
    setAddItem(null)
    onAdd?.(savedItem)
  }

  async function handleAllSubmit() {
    setIsSubmitted(true)
    if (allValid) {
      setIsConfirming(true)
      await handleSubmit?.(items)
      await requote?.run()
      setIsConfirming(false)
    }
  }

  async function handleFileUpload(file: File | File[] | null) {
    if (!file || Array.isArray(file) || !config.upload) {
      return
    }
    // Reading the file takes a while; the same toast then reports how it went.
    const toastId = toast.loading(`Importing ${file.name}…`)
    try {
      setIsImporting(true)
      const { items: imported, warning } = await config.upload(file, orgId)
      // The upload endpoint persisted the rows; reload from the server so the
      // table shows real records (real IDs, pending badges under an active policy).
      await queryClient.invalidateQueries({
        queryKey: [config.type === 'trucks' ? CacheKeys.TRUCKS : CacheKeys.TRAILERS, orgId],
      })
      invalidateApplicationSummary(queryClient, orgId)
      if (warning) {
        toast.warning(warning, { id: toastId })
      } else {
        toast.success(`${pluralize(config.label, imported.length, true)} imported`, { id: toastId })
      }
    } catch {
      toast.error('Error uploading file', { id: toastId })
    }
    setIsImporting(false)
  }

  function renderContent() {
    if (items.length === 0) {
      if (readOnly) {
        return (
          <div className="mt-10 mb-10 flex w-full flex-col items-center justify-center">
            <p className="text-muted-foreground text-sm">No {config.labelPlural} found</p>
          </div>
        )
      }

      if (compact) return null

      return (
        <div className="mt-10 mb-10 flex w-full flex-col items-center justify-center gap-5">
          <Button
            variant="default"
            onClick={() => setAddItem(config.createEmpty())}
            disabled={changesDisabled}
          >
            <CirclePlus />
            Add {config.label}
          </Button>

          {config.upload && (
            <>
              <div className="text-ink-400 flex items-center justify-center gap-2 text-sm">
                <Separator className="bg-ink-100 w-[100px]! shrink" /> or{' '}
                <Separator className="bg-ink-100 w-[100px]! shrink" />
              </div>

              <Dropzone
                onChange={handleFileUpload}
                disabled={changesDisabled}
                accept={PARSER_ACCEPTED_FILE_TYPES}
                maxSizeMB={10}
                isLoading={isImporting}
                key={`${items?.length}-${isImporting}`}
              />
            </>
          )}
        </div>
      )
    }

    return (
      <>
        {!readOnly && !addCtaOnTop && !compact && (
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setAddItem(config.createEmpty())}
              disabled={changesDisabled}
            >
              <CirclePlus />
              Add {config.label}
            </Button>
            {config.upload && (
              <UploadIconButton
                onUpload={handleFileUpload}
                disabled={changesDisabled}
                isLoading={isImporting}
                label="Upload documents"
              />
            )}
          </div>
        )}

        <ResponsiveTable
          items={pageItems}
          columns={columns}
          getRowId={(item) => item.id}
          // No row banner says what's missing unless carriers' requirements are checked, so the title flags it there.
          cardTitle={(item) => equipmentTitle(item, !enforceCarrierRequired)}
          // Where rows are being finished (the review, the chat, Intake) a row opens its edit form. Elsewhere,
          // and for a row that can't be edited yet, the read-only recap, which shows what the form doesn't.
          onRowClick={(item) =>
            enforceCarrierRequired && !readOnly && !getPending(item)
              ? setEditItem(item)
              : setDetailsItemId(item.id)
          }
          rowActions={!readOnly || hasPending ? renderRowActions : undefined}
          empty={`No ${config.labelPlural} match “${query}”`}
          rowIssue={enforceCarrierRequired ? (item) => equipmentIssue(item, config) : undefined}
          caption={
            !readOnly && (footer || handleSubmit || removedItems.length > 0) ? (
              <div className="relative flex w-full flex-wrap items-center gap-2">
                {removedItems.length > 0 && removedToggle}
                <div className="ml-auto flex items-center gap-2">
                  {footer}
                  {handleSubmit && (
                    <Button
                      variant="default"
                      onClick={handleAllSubmit}
                      loading={isConfirming ? (requote?.pendingLabel ?? 'Confirming…') : undefined}
                    >
                      {requote?.label ?? `Done adding ${config.labelPlural}`}
                    </Button>
                  )}
                </div>
              </div>
            ) : undefined
          }
        />

        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          summary={rangeSummary(config.labelPlural)}
        />
        {compact && !readOnly && <div className="mt-3 flex justify-end">{rosterActions}</div>}
      </>
    )
  }

  const showSearch = searchable && (items.length > SEARCH_MIN_ROWS || query.length > 0)
  const showTopActions = !readOnly && addCtaOnTop && !!items.length
  // An empty row would still add its margin, sinking the empty state below other tabs'.
  const showHeader = !hideHeading || showSearch || showTopActions

  const rosterActions = (
    <div className="ml-auto flex flex-wrap items-center gap-2">
      <Button variant="link" onClick={() => setAddItem(config.createEmpty())} disabled={changesDisabled}>
        <CirclePlus />
        Add {config.label}
      </Button>
      {config.upload && (
        <UploadIconButton
          onUpload={handleFileUpload}
          disabled={changesDisabled}
          isLoading={isImporting}
          label="Upload"
          ariaLabel={`Upload ${config.labelPlural}`}
        />
      )}
    </div>
  )

  return (
    <>
      {compact ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="mr-auto flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="text-ink text-md leading-none font-semibold">{heading}</span>
            {notice}
          </div>
          {/* Kept while a query filters, or a shrinking roster would hide what it's hiding. */}
          {searchable && (items.length >= SEARCH_MIN_ROWS || query.length > 0) && (
            <SearchInput
              value={query}
              onValueChange={onQueryChange}
              placeholder={`Search ${config.labelPlural}...`}
              aria-label={`Search ${config.labelPlural}`}
            />
          )}
          {/* Up top only while there's no list yet; a populated roster has them under its rows. */}
          {!readOnly && !items.length && rosterActions}
        </div>
      ) : (
        showHeader && (
          <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
            {!hideHeading && <div className="text-2xl font-semibold">{heading}</div>}
            {showSearch && (
              <SearchInput
                value={query}
                onValueChange={onQueryChange}
                className="mr-auto"
                placeholder={`Search ${config.labelPlural}...`}
                aria-label={`Search ${config.labelPlural}`}
              />
            )}
            {showTopActions && rosterActions}
          </div>
        )
      )}
      {!compact && !hideHeading && <OnboardingBannerSlot className="mb-4" />}

      {isSubmitted && errors.length > 0 && (
        <div className="mb-3 flex w-full flex-col gap-6">
          <Item variant="outline" className="bg-error text-white">
            <ItemMedia variant="icon" className="border-none bg-transparent text-white">
              <CircleAlert className="h-10! w-10! text-white" />
            </ItemMedia>
            <ItemContent>
              <ItemTitle className="capitalize">{config.labelPlural} with errors</ItemTitle>
              <ItemDescription className="text-white">
                {errors.map((error) => `${error.vin}`).join(', ')}
              </ItemDescription>
            </ItemContent>
          </Item>
        </div>
      )}

      {renderContent()}

      {!readOnly && removedItems.length > 0 && (items.length === 0 || showRemoved) && (
        <div className="mt-4 flex flex-col gap-2">
          {/* Wrapped, or the column stretches it and its label sits centred; Drivers keeps it left. */}
          {items.length === 0 && <div>{removedToggle}</div>}
          {showRemoved && (
            <ResponsiveTable
              className="opacity-60"
              items={removedItems}
              columns={removedEquipmentColumns(config)}
              getRowId={(item) => item.id}
              cardTitle={(item) => `${item.year} ${item.make} ${item.model}`}
              rowActions={(item) => (
                <Button
                  variant="iconOnly"
                  className="hover:text-cai-blue text-neutral-500"
                  onClick={() => handleRestore(item)}
                  disabled={changesDisabled}
                  title="Restore"
                >
                  <RotateCcw />
                </Button>
              )}
            />
          )}
        </div>
      )}

      {editItem && (
        <EditEquipmentModal
          item={editItem}
          isNew={false}
          existingItems={items.filter((i) => i.id !== editItem.id)}
          onClose={closeEdit}
          onSubmit={handleEditSubmit}
          config={config}
          enforceCarrierRequired={enforceCarrierRequired}
        />
      )}
      {detailsItem && (
        <EquipmentDetailsDialog
          item={detailsItem}
          label={config.label}
          open
          onOpenChange={(o) => !o && setDetailsItemId(null)}
          onEdit={
            !readOnly && !changesDisabled && !getPending(detailsItem)
              ? () => openEditFromDetails(detailsItem)
              : undefined
          }
        />
      )}
      {addItem && (
        <EditEquipmentModal
          item={addItem}
          isNew
          existingItems={items}
          onClose={() => setAddItem(null)}
          onSubmit={handleAddSubmit}
          config={config}
          enforceCarrierRequired={enforceCarrierRequired}
        />
      )}
    </>
  )
}

interface EditEquipmentFormData {
  vehicleNumber: string
  vin: string
  make: string
  model: string
  year: number
  licensePlate: string
  licenseState: string
  vehicleClass: VehicleClass | null
  weightClass: WeightClass | null
  price: number | null
}

const SERVER_FIELD_MAP: Record<string, keyof EditEquipmentFormData> = {
  vin: 'vin',
  make: 'make',
  model: 'model',
  year: 'year',
  license_plate: 'licensePlate',
  license_state: 'licenseState',
  vehicle_class: 'vehicleClass',
  weight_class: 'weightClass',
  price: 'price',
}

const GENERIC_SAVE_ERROR = 'Something went wrong saving this. Try again.'

/** Soft amber on a field the VIN lookup left empty. Signals "still needed", not an error. */
const MISSING_HIGHLIGHT = 'border-(--amber)/40 bg-(--amber)/15'

/** Each decoded answer fades in this long after the one before it. */
const VIN_CASCADE_STEP_MS = 150

const AUTO_FILLED_ANIMATION = 'answer-fill-in'

function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/** The fields a VIN lookup fills in. */
const VIN_FILLED_FIELDS: (keyof EditEquipmentFormData)[] = [
  'make',
  'model',
  'year',
  'vehicleClass',
  'weightClass',
]

interface VinFill {
  name: keyof EditEquipmentFormData
  /** Writes the answer if its field is still empty; says whether it did. */
  apply: () => boolean
}

/** The make, model and year a value suggestion was looked up for. */
function suggestionInputs(
  make: string | undefined,
  model: string | undefined,
  year: number | null | undefined
) {
  return [make?.trim(), model?.trim(), year || ''].join('|')
}

/** Pulls the first readable string out of a DRF error value, which may be nested arrays. */
function firstMessage(value: unknown): string | null {
  if (typeof value === 'string') return value.trim() || null
  if (Array.isArray(value)) {
    for (const entry of value) {
      const message = firstMessage(entry)
      if (message) return message
    }
  }
  return null
}

/** The roster's add and edit form for a truck or trailer; onSubmit rejects with the API error to show it on the fields. */
export function EditEquipmentModal({
  item,
  isNew,
  existingItems,
  onClose,
  onSubmit,
  config,
  enforceCarrierRequired,
  saveOnClose,
  children,
}: {
  item: Equipment
  isNew: boolean
  existingItems: Equipment[]
  onClose: () => void
  onSubmit: (item: Equipment) => Promise<void>
  config: EquipmentTableConfig
  enforceCarrierRequired?: boolean
  /** Owner-operator path: Done and closing both save what's entered, complete or not. */
  saveOnClose?: boolean
  /** Fields a caller adds after the vehicle's own, such as what a policy change also needs. */
  children?: ReactNode
}) {
  const weightClassesConfig = useWeightClassesConfig()
  // The cascade's weight step runs after renders that may have loaded the config, so it reads the latest.
  const weightClassesConfigRef = useRef(weightClassesConfig)
  useEffect(() => {
    weightClassesConfigRef.current = weightClassesConfig
  }, [weightClassesConfig])
  const [isSaving, setIsSaving] = useState(false)
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    setError,
    clearErrors,
    trigger,
    formState: { isSubmitting },
  } = useForm<EditEquipmentFormData>({
    // Opened to finish an incomplete one, its errors show at once, so each field checks again as it changes.
    mode: enforceCarrierRequired && !isNew ? 'onChange' : 'onSubmit',
    defaultValues: {
      vehicleNumber: item.vehicleNumber,
      vin: item.vin,
      make: item.make,
      model: item.model,
      year: item.year,
      licensePlate: item.licensePlate,
      licenseState: item.licenseState,
      vehicleClass: item.vehicleClass || null,
      weightClass: item.weightClass || null,
      price: item.price ?? null,
    },
  })

  const [isDecodingVin, setIsDecodingVin] = useState(false)
  const [vinLookupFailed, setVinLookupFailed] = useState(false)
  const lastLookedUpVin = useRef('')
  const [highlightMissing, setHighlightMissing] = useState(false)
  const [autoFilled, setAutoFilled] = useState<Set<keyof EditEquipmentFormData>>(() => new Set())
  const [trailerVinWarning, setTrailerVinWarning] = useState<string | null>(null)
  const checkDigitWarning = vinCheckDigitWarning(useWatch({ control, name: 'vin' }))
  // A value the user typed, even one that matches a suggestion, is never replaced by one.
  const priceTypedByUser = useRef(false)
  const autoFilledPrice = useRef(false)
  const isCascading = useRef(false)
  // The running cascade's unwritten answers, so a save can write them first instead of losing them.
  const activeCascade = useRef<{ vin: string; remaining: VinFill[] } | null>(null)
  const heldSuggestion = useRef<{ request: number; inputs: string; value: number } | null>(null)
  // Only the latest request may fill: one sent before year arrived can come back after the one sent with it.
  const latestSuggestionRequest = useRef(0)

  const fillSuggestedPrice = useCallback(
    (suggestedValue: number) => {
      if (priceTypedByUser.current) return
      // A saved value on an existing truck counts as the user's, even though it wasn't typed here.
      if (getValues('price') != null && !autoFilledPrice.current) return
      autoFilledPrice.current = true
      setValue('price', suggestedValue, { shouldDirty: true })
      setAutoFilled((filled) => new Set(filled).add('price'))
    },
    [getValues, setValue]
  )
  // A filled value belongs to the make and model it was looked up for, so it goes when they no longer match one.
  const clearAutoFilledPrice = useCallback(() => {
    if (!autoFilledPrice.current || priceTypedByUser.current) return
    autoFilledPrice.current = false
    setValue('price', null, { shouldDirty: true })
  }, [setValue])
  const suggestFn = config.type === 'trucks' ? suggestTruckValue : suggestTrailerValue

  const watchedValues = useWatch({ control })
  const watchedMake = useWatch({ control, name: 'make' })
  const watchedModel = useWatch({ control, name: 'model' })
  const watchedYear = useWatch({ control, name: 'year' })

  useEffect(() => {
    const make = watchedMake?.trim()
    const model = watchedModel?.trim()
    // Counted before the empty check, so clearing make or model also drops a request still in flight.
    const request = ++latestSuggestionRequest.current
    if (!make || !model) {
      clearAutoFilledPrice()
      return
    }
    suggestFn({
      make,
      model,
      year: watchedYear || undefined,
    })
      .then(({ suggestedValue }) => {
        if (request !== latestSuggestionRequest.current) return
        if (suggestedValue == null) clearAutoFilledPrice()
        // Value comes after weight class, so a suggestion that lands mid-cascade waits for it to finish.
        else if (isCascading.current)
          heldSuggestion.current = {
            request,
            inputs: suggestionInputs(make, model, watchedYear),
            value: suggestedValue,
          }
        else fillSuggestedPrice(suggestedValue)
      })
      .catch(() => {
        if (request === latestSuggestionRequest.current) clearAutoFilledPrice()
      })
  }, [watchedMake, watchedModel, watchedYear, suggestFn, fillSuggestedPrice, clearAutoFilledPrice])

  function isUsableVehicleClass(value: string | undefined) {
    return Boolean(value && config.vehicleClasses.includes(value as VehicleClass))
  }

  /** Did the decode return anything this form could take? A class it rejects doesn't count. */
  function vinDataUsable(data: VinDecodeResult) {
    return Boolean(
      data.make || data.model || data.year || data.weightClass || isUsableVehicleClass(data.vehicleClass)
    )
  }

  /** The decoded answers this form takes, in the order the fields appear. */
  function vinFills(data: VinDecodeResult): VinFill[] {
    // shouldValidate clears any error a failed submit left on the field we're filling.
    const fill = { shouldValidate: true, shouldDirty: true }
    const { make, model, year, vehicleClass, weightClass } = data
    // Each write lands a step later, so emptiness is checked again then: the user may have typed in the meantime.
    const fillIfEmpty =
      <K extends 'make' | 'model' | 'year' | 'vehicleClass' | 'weightClass'>(
        name: K,
        value: PathValue<EditEquipmentFormData, K>
      ) =>
      () => {
        if (getValues(name)) return false
        setValue(name, value, fill)
        return true
      }
    // The class filled a step earlier may not offer this weight, and its clean-up effect has already run.
    const weightFitsClass = (weight: WeightClass) => {
      const currentClass = getValues('vehicleClass')
      const valid = currentClass && weightClassesConfigRef.current?.[currentClass]
      return !valid || valid.includes(weight)
    }
    return [
      make && !getValues('make') && { name: 'make' as const, apply: fillIfEmpty('make', make) },
      model && !getValues('model') && { name: 'model' as const, apply: fillIfEmpty('model', model) },
      year && !getValues('year') && { name: 'year' as const, apply: fillIfEmpty('year', year) },
      vehicleClass &&
        !getValues('vehicleClass') &&
        isUsableVehicleClass(vehicleClass) && {
          name: 'vehicleClass' as const,
          apply: fillIfEmpty('vehicleClass', vehicleClass as VehicleClass),
        },
      weightClass &&
        !getValues('weightClass') && {
          name: 'weightClass' as const,
          apply: () =>
            weightFitsClass(weightClass as WeightClass) &&
            fillIfEmpty('weightClass', weightClass as WeightClass)(),
        },
    ].filter((entry) => !!entry)
  }

  function applyVinFill({ name, apply }: VinFill) {
    if (apply()) setAutoFilled((filled) => new Set(filled).add(name))
  }

  /** Ends the cascade; a value suggestion it held fills only if the VIN it was for is still in the field. */
  function endCascade(vinStillCurrent: boolean) {
    activeCascade.current = null
    isCascading.current = false
    const held = heldSuggestion.current
    heldSuggestion.current = null
    // A flush can write year after the suggestion came back without it, before the next lookup is even sent.
    const { make, model, year } = getValues()
    if (held && held.inputs !== suggestionInputs(make, model, year)) return
    if (vinStillCurrent && held && held.request === latestSuggestionRequest.current)
      fillSuggestedPrice(held.value)
  }

  /** Writes whatever the cascade hasn't yet, so a save mid-cascade keeps every decoded answer. */
  function flushCascade() {
    const run = activeCascade.current
    if (!run) return
    const vinStillCurrent = isVinStillCurrent(run.vin)
    if (vinStillCurrent) run.remaining.splice(0).forEach(applyVinFill)
    endCascade(vinStillCurrent)
  }

  /** Fills the decoded answers one at a time, in field order, then the held value suggestion. */
  async function cascadeVinData(vin: string, data: VinDecodeResult) {
    const fills = vinFills(data)
    if (!fills.length) return
    const run = { vin, remaining: fills }
    activeCascade.current = run
    isCascading.current = true
    while (run.remaining.length) {
      await wait(VIN_CASCADE_STEP_MS)
      // A save flushed it, or a newer lookup replaced it.
      if (activeCascade.current !== run) return
      if (!isVinStillCurrent(vin)) {
        endCascade(false)
        forgetLookup(vin)
        return
      }
      const next = run.remaining.shift()
      if (next) applyVinFill(next)
    }
    await wait(VIN_CASCADE_STEP_MS)
    if (activeCascade.current === run) endCascade(isVinStillCurrent(vin))
  }

  /** The field may have been edited mid-request, with or without starting a new lookup. */
  function isVinStillCurrent(vin: string) {
    return sanitizeVin(getValues('vin')) === vin
  }

  /** An abandoned lookup must not block the same VIN when it's typed back in. */
  function forgetLookup(vin: string) {
    if (lastLookedUpVin.current === vin) lastLookedUpVin.current = ''
  }

  async function runVinLookup(vin: string) {
    // The ref dedupes: a full VIN fires on change, and blur must not repeat it.
    if (vin.length < 11 || vin === lastLookedUpVin.current) return
    lastLookedUpVin.current = vin
    // Clearing up front, not on arrival, keeps the error off the fields for the round trip.
    const fillable = VIN_FILLED_FIELDS.filter((name) => !getValues(name))
    setIsDecodingVin(true)
    setHighlightMissing(false)
    setVinLookupFailed(false)
    setAutoFilled(new Set())
    if (fillable.length) clearErrors(fillable)
    let data: VinDecodeResult
    try {
      data = await decodeVin(vin)
    } catch {
      if (isVinStillCurrent(vin)) setVinLookupFailed(true)
      return
    } finally {
      // The ref, not the field: a newer lookup owns the spinner from here.
      if (lastLookedUpVin.current === vin) setIsDecodingVin(false)
    }
    if (!isVinStillCurrent(vin)) {
      forgetLookup(vin)
      return
    }
    setVinLookupFailed(!vinDataUsable(data))
    // A heuristic from NHTSA, not a fact about this fleet, so it warns rather than blocks.
    if (config.type === 'trucks' && data.isTrailer) {
      setTrailerVinWarning('This VIN belongs to a trailer. Add it under Trailers.')
    }
    await cascadeVinData(vin, data)
    // Amber waits for the cascade, or a field about to be filled would flash it first.
    if (isVinStillCurrent(vin)) setHighlightMissing(true)
  }

  function autoFilledClass(name: keyof EditEquipmentFormData) {
    return autoFilled.has(name) ? AUTO_FILLED_ANIMATION : undefined
  }

  // An error already says "fix this", so amber stands down when one is showing.
  function missingClass(name: keyof EditEquipmentFormData, hasError: boolean) {
    return !hasError && highlightMissing && !watchedValues[name] ? MISSING_HIGHLIGHT : undefined
  }

  const selectedVehicleClass = watch('vehicleClass')
  const allClasses = enumToOptions(VehicleClassLabels).filter((label) =>
    config.vehicleClasses.includes(label.value as VehicleClass)
  )

  const weightClassOptions = useMemo(() => {
    const allOptions = enumToOptions(WeightClassLabels)
    if (!selectedVehicleClass || !weightClassesConfig) return allOptions
    const validWeights = weightClassesConfig[selectedVehicleClass]
    if (!validWeights) return allOptions
    return allOptions.filter((option) => validWeights.includes(option.value as WeightClass))
  }, [selectedVehicleClass, weightClassesConfig])

  useEffect(() => {
    if (!selectedVehicleClass || !weightClassesConfig) return
    const validWeights = weightClassesConfig[selectedVehicleClass]
    if (!validWeights) return
    const currentWeight = getValues('weightClass')
    if (currentWeight && !validWeights.includes(currentWeight)) {
      setValue('weightClass', null)
    }
  }, [selectedVehicleClass, weightClassesConfig, setValue, getValues])

  function applyServerErrors(err: unknown) {
    const data = (err as { response?: { data?: unknown } })?.response?.data
    const bodyMessage = firstMessage(data)
    if (bodyMessage) {
      toast.error(bodyMessage)
      return
    }
    if (!data || typeof data !== 'object') {
      toast.error(GENERIC_SAVE_ERROR)
      return
    }

    // Anything the form can't show inline still has to reach the user, so it goes to a toast.
    const unmapped: string[] = []
    let surfacedInline = false
    Object.entries(data as Record<string, unknown>).forEach(([apiField, value]) => {
      const message = firstMessage(value)
      if (!message) return
      const formField = SERVER_FIELD_MAP[apiField]
      if (formField) {
        setError(formField, { message, type: 'server' })
        surfacedInline = true
      } else {
        unmapped.push(message)
      }
    })

    if (unmapped.length) toast.error(unmapped.join(' '))
    else if (!surfacedInline) toast.error(GENERIC_SAVE_ERROR)
  }

  async function _onSubmit(data: EditEquipmentFormData) {
    const sanitized: EditEquipmentFormData = {
      ...data,
      vin: sanitizeVin(data.vin),
      licensePlate: sanitizeLicensePlate(data.licensePlate),
      licenseState: sanitizeLicenseState(data.licenseState),
    }
    try {
      await onSubmit({
        ...item,
        vehicleNumber: sanitized.vehicleNumber,
        vin: sanitized.vin,
        make: sanitized.make,
        model: sanitized.model,
        year: sanitized.year,
        licensePlate: sanitized.licensePlate,
        licenseState: sanitized.licenseState,
        vehicleClass: sanitized.vehicleClass,
        weightClass: sanitized.weightClass,
        // An untouched suggestion goes as null so the backend saves its own estimate, marked as one.
        price: autoFilledPrice.current && !priceTypedByUser.current ? null : sanitized.price,
      })
    } catch (err) {
      applyServerErrors(err)
    }
  }

  function findDuplicateVin(value: string): Equipment | undefined {
    const target = sanitizeVin(value)
    if (!target) return undefined
    return existingItems.find((other) => sanitizeVin(other.vin) === target)
  }

  function findDuplicatePlate(value: string, state: string): Equipment | undefined {
    const plate = sanitizeLicensePlate(value)
    if (!plate) return undefined
    const stateUpper = sanitizeLicenseState(state)
    return existingItems.find(
      (other) =>
        sanitizeLicensePlate(other.licensePlate) === plate &&
        sanitizeLicenseState(other.licenseState) === stateUpper
    )
  }

  async function saveAsEntered() {
    if (isSaving) return
    flushCascade()
    setIsSaving(true)
    // A date field commits a cleared value on blur, which Escape skips; blur it first so the save sees it.
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur()
    await _onSubmit(getValues())
    setIsSaving(false)
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!saveOnClose) onClose()
        else if (!open) void saveAsEntered()
      }}
    >
      <FormDialogContent
        title={`${isNew ? 'Add' : 'Edit'} ${config.label}`}
        size="sm"
        form={{
          onSubmit: saveOnClose
            ? (e) => {
                e.preventDefault()
                void saveAsEntered()
              }
            : (e) => {
                flushCascade()
                return handleSubmit(_onSubmit)(e)
              },
          // Enter in a text field would implicitly submit and close the dialog mid-entry.
          onKeyDown: (e) => {
            if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
              e.preventDefault()
            }
          },
        }}
        closeDisabled={isSubmitting || isSaving}
        // Opened to finish an incomplete one, it shows what's missing straight away; the fields only exist once
        // the dialog's content has mounted, which is when this fires.
        onOpenAutoFocus={() => {
          if (enforceCarrierRequired && !isNew) void trigger()
        }}
        footer={
          <>
            {!enforceCarrierRequired && (
              <p className="text-muted-foreground mr-auto self-center text-xs">
                Missing something? You can add it later
              </p>
            )}
            {saveOnClose ? (
              <Button type="submit" loading={isSaving ? 'Saving…' : undefined}>
                Done
              </Button>
            ) : (
              <>
                <DialogClose asChild>
                  <Button type="button" variant="outline" disabled={isSubmitting}>
                    Cancel
                  </Button>
                </DialogClose>
                <Button type="submit" loading={isSubmitting ? 'Saving…' : undefined}>
                  Save changes
                </Button>
              </>
            )}
          </>
        }
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="vin">VIN</FieldLabel>
            <Controller
              render={({ field, fieldState }) => (
                <>
                  <div className="relative">
                    <Input
                      {...field}
                      id="vin"
                      placeholder="1FUJGLDR0LLAA1111"
                      aria-invalid={fieldState.error ? true : false}
                      className={cn(isDecodingVin && 'pr-10')}
                      onChange={(e) => {
                        field.onChange(e)
                        // Until the first save the form checks only on submit, so an error already
                        // showing is checked again as the VIN is fixed.
                        if (fieldState.error) void trigger('vin')
                        if (vinLookupFailed) setVinLookupFailed(false)
                        setTrailerVinWarning(null)
                        const sanitized = sanitizeVin(e.target.value)
                        if (sanitized.length === FULL_VIN_LENGTH) runVinLookup(sanitized)
                      }}
                      onBlur={(e) => {
                        field.onBlur()
                        const sanitized = sanitizeVin(e.target.value)
                        if (sanitized !== e.target.value) {
                          setValue('vin', sanitized, { shouldValidate: true })
                        }
                        if (sanitized) runVinLookup(sanitized)
                      }}
                    />
                    {isDecodingVin && (
                      <Spinner className="text-muted-foreground absolute top-1/2 right-3 -translate-y-1/2" />
                    )}
                  </div>
                  <FieldError>{fieldState.error?.message}</FieldError>
                  {!fieldState.error && (trailerVinWarning || checkDigitWarning) && (
                    <p className="text-warning-d1 mt-1 text-xs">{trailerVinWarning || checkDigitWarning}</p>
                  )}
                  {/* The trailer warning already explains the decode, so don't also claim no match. */}
                  {vinLookupFailed && !fieldState.error && !trailerVinWarning && !checkDigitWarning && (
                    <FieldDescription>
                      {"We couldn't find that VIN — fill in the details below."}
                    </FieldDescription>
                  )}
                </>
              )}
              name="vin"
              control={control}
              rules={{
                required: enforceCarrierRequired ? 'VIN is required.' : false,
                validate: (value: string) => {
                  // Blocks whatever every carrier would reject; the check digit only warns.
                  const error = vinError(value)
                  if (error) return error
                  const dup = findDuplicateVin(value)
                  if (!dup) return true
                  const label = dup.vehicleNumber || dup.vin
                  return `This VIN is already used on ${config.label} ${label}.`
                },
              }}
            />
          </Field>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-[12rem_1fr] sm:gap-2">
            <Field>
              <FieldLabel htmlFor="licenseState" optional>
                Registered state
              </FieldLabel>
              <Controller
                render={({ field, fieldState }) => (
                  <>
                    <StateSelect
                      error={fieldState.error ? true : false}
                      value={field.value}
                      onChange={(value: string) => {
                        const sanitized = sanitizeLicenseState(value)
                        field.onChange(sanitized)
                      }}
                      clearable
                    />
                    <FieldError>{fieldState.error?.message}</FieldError>
                  </>
                )}
                name="licenseState"
                control={control}
                rules={{
                  pattern: {
                    value: /^[A-Z]{2}$/i,
                    message: 'Pick a state from the list.',
                  },
                }}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="licensePlate" optional>
                License plate
              </FieldLabel>
              <Controller
                render={({ field, fieldState }) => (
                  <>
                    <Input
                      {...field}
                      id="licensePlate"
                      placeholder="8ABC123"
                      aria-invalid={fieldState.error ? true : false}
                      onBlur={(e) => {
                        field.onBlur()
                        const sanitized = sanitizeLicensePlate(e.target.value)
                        if (sanitized !== e.target.value) {
                          setValue('licensePlate', sanitized, { shouldValidate: true })
                        }
                      }}
                    />
                    <FieldError>{fieldState.error?.message}</FieldError>
                  </>
                )}
                name="licensePlate"
                control={control}
                rules={{
                  validate: (value: string) => {
                    const state = getValues('licenseState')
                    if (!value || !state) return true
                    const dup = findDuplicatePlate(value, state)
                    if (!dup) return true
                    const label = dup.vehicleNumber || dup.vin
                    return `This plate is already used on ${config.label} ${label}.`
                  },
                }}
              />
            </Field>
          </div>

          {/* VIN, state and plate are all we ask for; the VIN fills in everything below the divider. */}
          <Separator />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 sm:gap-2">
            <Field>
              <FieldLabel htmlFor="make">Make</FieldLabel>
              <Controller
                render={({ field, fieldState }) => (
                  <>
                    <Input
                      {...field}
                      id="make"
                      placeholder={config.makePlaceholder}
                      aria-invalid={fieldState.error ? true : false}
                      className={cn(missingClass('make', !!fieldState.error), autoFilledClass('make'))}
                    />
                    <FieldError>{fieldState.error?.message}</FieldError>
                  </>
                )}
                name="make"
                control={control}
                rules={{ required: enforceCarrierRequired ? 'Make is required.' : false }}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="model">Model</FieldLabel>
              <Controller
                render={({ field, fieldState }) => (
                  <>
                    <Input
                      {...field}
                      id="model"
                      placeholder={config.modelPlaceholder}
                      aria-invalid={fieldState.error ? true : false}
                      className={cn(missingClass('model', !!fieldState.error), autoFilledClass('model'))}
                    />
                    <FieldError>{fieldState.error?.message}</FieldError>
                  </>
                )}
                name="model"
                control={control}
                rules={{ required: enforceCarrierRequired ? 'Model is required.' : false }}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="year">Year</FieldLabel>
              <Controller
                render={({ field, fieldState }) => (
                  <>
                    <YearSelect
                      error={fieldState.error ? true : false}
                      value={field.value ? String(field.value) : undefined}
                      onChange={field.onChange}
                      className={cn(missingClass('year', !!fieldState.error), autoFilledClass('year'))}
                    />
                    <FieldError>{fieldState.error?.message}</FieldError>
                  </>
                )}
                name="year"
                control={control}
                rules={{
                  validate: (value: number) =>
                    !enforceCarrierRequired || Boolean(value) || 'Year is required.',
                }}
              />
            </Field>
          </div>

          <div
            className={cn('grid grid-cols-1 gap-4 sm:gap-2', config.type === 'trucks' && 'sm:grid-cols-2')}
          >
            <Field>
              <FieldLabel htmlFor="vehicleClass">{config.classFieldLabel}</FieldLabel>
              <Controller
                render={({ field, fieldState }) => (
                  <>
                    <SelectWithMore
                      aria-invalid={fieldState.error ? true : false}
                      commonOptions={allClasses.filter((option) =>
                        config.commonVehicleClasses.includes(option.value as VehicleClass)
                      )}
                      moreOptions={allClasses.filter(
                        (option) => !config.commonVehicleClasses.includes(option.value as VehicleClass)
                      )}
                      value={field.value || undefined}
                      onValueChange={field.onChange}
                      placeholder="Select class..."
                      className={cn(
                        'w-full',
                        missingClass('vehicleClass', !!fieldState.error),
                        autoFilledClass('vehicleClass')
                      )}
                      error={fieldState.error ? true : false}
                    />
                    <FieldError>{fieldState.error?.message}</FieldError>
                  </>
                )}
                name="vehicleClass"
                control={control}
                rules={{
                  required: enforceCarrierRequired ? `${config.classFieldLabel} is required.` : false,
                  // A class the dropdown doesn't offer (e.g. a trailer class on a truck) renders
                  // as an empty trigger, so treat it as unset and make the user pick a real one.
                  validate: (value: VehicleClass | null) =>
                    !value ||
                    config.vehicleClasses.includes(value) ||
                    `${VehicleClassLabels[value]} isn't a valid ${config.label} class — pick one from the list.`,
                }}
              />
            </Field>

            {config.type === 'trucks' && (
              <Field>
                <FieldLabel htmlFor="weightClass">Weight class</FieldLabel>
                <Controller
                  render={({ field, fieldState }) => (
                    <>
                      <SimpleSelect
                        options={weightClassOptions}
                        value={field.value || undefined}
                        onValueChange={field.onChange}
                        error={!!fieldState.error}
                        placeholder="Select weight..."
                        className={cn(
                          'w-full',
                          missingClass('weightClass', !!fieldState.error),
                          autoFilledClass('weightClass')
                        )}
                      />
                      <FieldError>{fieldState.error?.message}</FieldError>
                    </>
                  )}
                  name="weightClass"
                  control={control}
                  rules={{ required: enforceCarrierRequired ? 'Weight class is required.' : false }}
                />
              </Field>
            )}
          </div>

          <Field>
            <FieldLabel htmlFor="price" optional>
              Value
            </FieldLabel>
            <Controller
              render={({ field, fieldState }) => (
                <>
                  <div className={autoFilledClass('price')}>
                    <CurrencyInput
                      id="price"
                      value={field.value}
                      onChange={(value) => {
                        priceTypedByUser.current = true
                        field.onChange(value)
                      }}
                      placeholder="140,000"
                      aria-invalid={fieldState.error ? true : false}
                    />
                  </div>
                  <FieldError>{fieldState.error?.message}</FieldError>
                </>
              )}
              name="price"
              control={control}
            />
          </Field>
          {children}
        </FieldGroup>
      </FormDialogContent>
    </Dialog>
  )
}
